/* istanbul ignore file */
import React from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { get, isEmpty, isNull } from 'lodash';
import { Autocomplete } from '@material-ui/lab';
import {
  TextField,
  Grid,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  FormHelperText,
} from '@material-ui/core';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { translate, arrayTranslate } from 'containers/services/translate';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import messages from 'containers/PreOpeningPage/messages';
import {
  brand,
  filtredCompanies,
  getByPlaceholderText,
  getOptions,
  getVehicleFromSagisInfo,
} from 'containers/PreOpeningPage/OpposingParty/utils';
import { Vehicule } from 'containers/PreOpeningPage/typings';
import { VEHICULE } from 'containers/data-structure';
import { displayErrorText } from 'containers/services/validateForm';

export interface OpposingPartyFormProps {
  intl: IntlShape;
  customClass: string;
  vehicule: Vehicule | null;
  setCurrentVehicule?: Function;
  readOnly?: boolean;
  handleChange?: Function;
  listings: any;
  isForDrawer: boolean;
  sagisOpposingParties: any;
  isRegistrationNumberValid?: Boolean;
  registrationNumberErrorText?: String;
  formValidator?: any;
  inputsLengthErrorText?: {
    [VEHICULE.POLICY_NUMBER_OPPOSING]: String;
    [VEHICULE.CERTIFICATE_NUMBER_OPPOSING]: String;
    [VEHICULE.CLAIM_NUMBER_OPPOSING]: String;
  };
}

const OpposingPartyForm: React.FC<OpposingPartyFormProps> = ({
  vehicule,
  customClass,
  handleChange,
  listings,
  formValidator,
  inputsLengthErrorText,
  ...props
}): JSX.Element => {
  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: getOptions,
  });
  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);
  const { message } = displayErrorText(
    formValidator,
    translateArrayErrorMessages(formValidator, true),
  );
  return (
    <>
      <Grid container direction={'row'} spacing={2}>
        {props.readOnly ? (
          <Grid item xs={props.isForDrawer ? 12 : 2}>
            <TextField
              label={translate(
                { ...messages.claimInformationVehicleBrand },
                props,
              )}
              value={
                featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO) &&
                !isEmpty(
                  getVehicleFromSagisInfo(
                    get(vehicule, 'partKey'),
                    props.sagisOpposingParties,
                  ),
                )
                  ? brand(
                      listings,
                      get(
                        getVehicleFromSagisInfo(
                          get(vehicule, 'partKey'),
                          props.sagisOpposingParties,
                        ),
                        'insuranceCompanyCode',
                      ),
                    )
                  : vehicule && vehicule[VEHICULE.BRAND]
              }
              className={customClass}
              onChange={handleChange && handleChange(VEHICULE.BRAND)}
              InputProps={{
                readOnly: props.readOnly,
              }}
              defaultValue={vehicule && vehicule.brand}
            />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Autocomplete
              id="standard-basic-xx"
              data-testid="autocomplete"
              className="formEl"
              options={get(listings, 'vehicleBrands', [])}
              getOptionLabel={getOptions}
              value={brand(listings, vehicule)}
              autoSelect={true}
              onChange={handleChange && handleChange(VEHICULE.BRAND)}
              filterOptions={filterOptions}
              renderInput={params => (
                <TextField
                  {...params}
                  className={customClass}
                  name={VEHICULE.BRAND}
                  label={translate(
                    { ...messages.claimInformationVehicleBrand },
                    props,
                  )}
                  error={!!get(formValidator, VEHICULE.BRAND)}
                  helperText={message[VEHICULE.BRAND]}
                />
              )}
            />
          </Grid>
        )}
        {featureService.hasFeature(FEATURES.RNVA) && (
          <Grid item xs={props.readOnly ? (props.isForDrawer ? 12 : 3) : 12}>
            {props.readOnly ? (
              <TextField
                label={translate({ ...messages.registrationType }, props)}
                className={customClass}
                value={
                  vehicule &&
                  get(listings, 'registrationNumberTypes', []) &&
                  get(listings, 'registrationNumberTypes', []).find(
                    (el: any) =>
                      el.id === get(vehicule, 'registrationNumberTypeId', null),
                  )?.title
                }
                onChange={
                  handleChange &&
                  handleChange(VEHICULE.REGISTRATION_NUMBER_TYPE)
                }
                InputProps={{
                  readOnly: props.readOnly,
                }}
              />
            ) : (
              <FormControl className="formEl">
                <InputLabel className={customClass}>
                  {translate({ ...messages.registrationType }, props)}
                </InputLabel>
                <Select
                  className={customClass}
                  onChange={
                    handleChange &&
                    handleChange(VEHICULE.REGISTRATION_NUMBER_TYPE)
                  }
                  name={VEHICULE.REGISTRATION_NUMBER_TYPE}
                  value={
                    vehicule && vehicule[VEHICULE.REGISTRATION_NUMBER_TYPE]
                      ? vehicule[VEHICULE.REGISTRATION_NUMBER_TYPE]
                      : get(vehicule, 'registrationNumberTypeId', '')
                  }
                >
                  {!isEmpty(get(listings, 'registrationNumberTypes', [])) &&
                    get(listings, 'registrationNumberTypes', []).map(
                      (el: any) => (
                        <MenuItem value={el.id} key={el.title}>
                          {el.title}
                        </MenuItem>
                      ),
                    )}
                </Select>
                {!!get(formValidator, VEHICULE.REGISTRATION_NUMBER_TYPE) && (
                  <FormHelperText className="error">
                    {message[VEHICULE.REGISTRATION_NUMBER_TYPE]}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          </Grid>
        )}
        <Grid item xs={props.readOnly ? (props.isForDrawer ? 12 : 2) : 12}>
          <TextField
            label={translate({ ...messages.registration }, props)}
            className={customClass}
            value={
              vehicule &&
              featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO) &&
              !isEmpty(
                getVehicleFromSagisInfo(
                  get(vehicule, 'partKey'),
                  props.sagisOpposingParties,
                ),
              )
                ? get(
                    getVehicleFromSagisInfo(
                      get(vehicule, 'partKey'),
                      props.sagisOpposingParties,
                    ),
                    'registrationNumber',
                  )
                : vehicule && vehicule[VEHICULE.REGISTRATION_NUMBER]
            }
            onChange={
              handleChange && handleChange(VEHICULE.REGISTRATION_NUMBER)
            }
            InputProps={{
              readOnly: props.readOnly,
              inputProps: { maxlength: 20 },
            }}
            error={
              (!featureService.hasFeature(FEATURES.RNVA) &&
                !props.isRegistrationNumberValid &&
                !props.readOnly) ||
              (featureService.hasFeature(FEATURES.RNVA) &&
                !props.readOnly &&
                !!props.registrationNumberErrorText) ||
              !!get(formValidator, VEHICULE.REGISTRATION_NUMBER)
                ? true
                : false
            }
            placeholder={
              vehicule && vehicule[VEHICULE.REGISTRATION_NUMBER_TYPE]
                ? getByPlaceholderText(
                    vehicule[VEHICULE.REGISTRATION_NUMBER_TYPE] as number,
                  )
                : ''
            }
            helperText={
              message[VEHICULE.REGISTRATION_NUMBER] ||
              (!featureService.hasFeature(FEATURES.RNVA) &&
                !props.isRegistrationNumberValid &&
                !props.readOnly &&
                translate(messages.registrationNumberAlert, props)) ||
              (featureService.hasFeature(FEATURES.RNVA) &&
                props.registrationNumberErrorText)
            }
          />
        </Grid>
        <Grid item xs={props.readOnly ? (props.isForDrawer ? 12 : 3) : 12}>
          {props.readOnly ? (
            <TextField
              label={translate({ ...messages.opposingCompanies }, props)}
              className={customClass}
              value={
                vehicule &&
                featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO) &&
                !isEmpty(
                  getVehicleFromSagisInfo(
                    get(vehicule, 'partKey'),
                    props.sagisOpposingParties,
                  ),
                )
                  ? get(
                      getVehicleFromSagisInfo(
                        get(vehicule, 'partKey'),
                        props.sagisOpposingParties,
                      ),
                      'insuranceCompanyName',
                    )
                  : vehicule && vehicule[VEHICULE.OPPOSING_PARTY]
              }
              onChange={handleChange && handleChange(VEHICULE.OPPOSING_PARTY)}
              InputProps={{
                readOnly: props.readOnly,
              }}
              defaultValue={vehicule && vehicule.opposingParty}
            />
          ) : (
            <FormControl className="formEl">
              <InputLabel className={customClass}>
                {translate({ ...messages.opposingCompanies }, props)}
              </InputLabel>
              <Select
                className={customClass}
                onChange={handleChange && handleChange(VEHICULE.OPPOSING_PARTY)}
                name={VEHICULE.OPPOSING_PARTY}
                value={get(vehicule, [VEHICULE.OPPOSING_PARTY], '')}
              >
                {!isEmpty(get(listings, 'compagnies', [])) &&
                  filtredCompanies(
                    listings!.compagnies,
                    listings!.insuranceCompanies,
                  ).map(company => (
                    <MenuItem value={company.ItemKey} key={company.ItemKey}>
                      {company.ItemDescription}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
        </Grid>

        <Grid item xs={props.readOnly ? (props.isForDrawer ? 12 : 2) : 12}>
          <TextField
            label={translate({ ...messages.opposingLastName }, props)}
            className={customClass}
            InputProps={{
              readOnly: props.readOnly,
            }}
            value={
              vehicule &&
              featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO) &&
              !isEmpty(
                getVehicleFromSagisInfo(
                  get(vehicule, 'partKey'),
                  props.sagisOpposingParties,
                ),
              )
                ? get(
                    getVehicleFromSagisInfo(
                      get(vehicule, 'partKey'),
                      props.sagisOpposingParties,
                    ),
                    'lastName',
                  )
                : vehicule && vehicule[VEHICULE.LAST_NAME]
            }
            onChange={handleChange && handleChange(VEHICULE.LAST_NAME)}
          />
        </Grid>

        <Grid item xs={props.readOnly ? (props.isForDrawer ? 12 : 2) : 12}>
          <TextField
            label={translate({ ...messages.opposingFirstName }, props)}
            className={customClass}
            value={
              vehicule &&
              featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO) &&
              !isEmpty(
                getVehicleFromSagisInfo(
                  get(vehicule, 'partKey'),
                  props.sagisOpposingParties,
                ),
              )
                ? get(
                    getVehicleFromSagisInfo(
                      get(vehicule, 'partKey'),
                      props.sagisOpposingParties,
                    ),
                    'firstName',
                  )
                : vehicule && vehicule[VEHICULE.FIRST_NAME]
            }
            InputProps={{
              readOnly: props.readOnly,
            }}
            onChange={handleChange && handleChange(VEHICULE.FIRST_NAME)}
          />
        </Grid>

        {featureService.hasFeature(FEATURES.RNVA) && (
          <>
            <Grid item xs={props.readOnly ? (props.isForDrawer ? 12 : 2) : 12}>
              <TextField
                label={translate(
                  { ...messages.claimDeclarationPoliceNumber },
                  props,
                )}
                className={customClass}
                value={vehicule && vehicule[VEHICULE.POLICY_NUMBER_OPPOSING]}
                InputProps={{
                  readOnly: props.readOnly,
                }}
                onChange={
                  handleChange && handleChange(VEHICULE.POLICY_NUMBER_OPPOSING)
                }
                error={
                  !!get(formValidator, VEHICULE.POLICY_NUMBER_OPPOSING) ||
                  !!get(inputsLengthErrorText, [
                    VEHICULE.POLICY_NUMBER_OPPOSING,
                  ])
                }
                helperText={
                  message[VEHICULE.POLICY_NUMBER_OPPOSING] ||
                  get(inputsLengthErrorText, [VEHICULE.POLICY_NUMBER_OPPOSING])
                }
              />
            </Grid>
            <Grid item xs={props.readOnly ? (props.isForDrawer ? 12 : 2) : 12}>
              <TextField
                label={translate(
                  { ...messages.claimDeclarationCertificateNumber },
                  props,
                )}
                className={customClass}
                value={
                  vehicule && vehicule[VEHICULE.CERTIFICATE_NUMBER_OPPOSING]
                }
                InputProps={{
                  readOnly: props.readOnly,
                }}
                onChange={
                  handleChange &&
                  handleChange(VEHICULE.CERTIFICATE_NUMBER_OPPOSING)
                }
                error={
                  !!get(formValidator, VEHICULE.CERTIFICATE_NUMBER_OPPOSING) ||
                  !!get(inputsLengthErrorText, [
                    VEHICULE.CERTIFICATE_NUMBER_OPPOSING,
                  ])
                }
                helperText={
                  message[VEHICULE.CERTIFICATE_NUMBER_OPPOSING] ||
                  get(inputsLengthErrorText, [
                    VEHICULE.CERTIFICATE_NUMBER_OPPOSING,
                  ])
                }
              />
            </Grid>
            <Grid item xs={props.readOnly ? (props.isForDrawer ? 12 : 2) : 12}>
              <TextField
                label={translate(
                  { ...messages.claimDeclarationClaimNumber },
                  props,
                )}
                className={customClass}
                value={vehicule && vehicule[VEHICULE.CLAIM_NUMBER_OPPOSING]}
                InputProps={{
                  readOnly: props.readOnly,
                }}
                onChange={
                  handleChange && handleChange(VEHICULE.CLAIM_NUMBER_OPPOSING)
                }
                error={
                  !!get(formValidator, VEHICULE.CLAIM_NUMBER_OPPOSING) ||
                  !!get(inputsLengthErrorText, [VEHICULE.CLAIM_NUMBER_OPPOSING])
                }
                helperText={
                  message[VEHICULE.CLAIM_NUMBER_OPPOSING] ||
                  get(inputsLengthErrorText, [VEHICULE.CLAIM_NUMBER_OPPOSING])
                }
              />
            </Grid>
            <Grid item xs={props.readOnly ? (props.isForDrawer ? 12 : 3) : 12}>
              {props.readOnly ? (
                <TextField
                  label={translate({ ...messages.responsability }, props)}
                  className={customClass}
                  value={vehicule && vehicule[VEHICULE.RESPONSABILITY_RATE]}
                  onChange={
                    handleChange && handleChange(VEHICULE.RESPONSABILITY)
                  }
                  InputProps={{
                    readOnly: props.readOnly,
                  }}
                  defaultValue={
                    vehicule && get(vehicule, 'responsabilityRate', '')
                  }
                />
              ) : (
                <FormControl className="formEl">
                  <InputLabel className={customClass}>
                    {translate({ ...messages.responsability }, props)}
                  </InputLabel>
                  <Select
                    className={customClass}
                    onChange={
                      handleChange && handleChange(VEHICULE.RESPONSABILITY)
                    }
                    name={VEHICULE.RESPONSABILITY}
                    value={get(vehicule, [VEHICULE.RESPONSABILITY], '')}
                  >
                    {!isEmpty(get(listings, 'responsability', [])) &&
                      get(listings, 'responsability', []).map((el: any) => (
                        <MenuItem value={el.ItemKey} key={el.ItemDescription}>
                          {el.ItemDescription}
                        </MenuItem>
                      ))}
                  </Select>
                  {!!get(formValidator, VEHICULE.RESPONSABILITY) && (
                    <FormHelperText className="error">
                      {message[VEHICULE.RESPONSABILITY]}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default injectIntl(OpposingPartyForm);
